// SendNotificationForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPaperPlane,
  faUsers,
  faUser,
  faUserTag,
  faStar,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faServer,
  faSpinner,
  faSearch,
  faUserPlus,
  faUserCheck,
  faUserMinus,
  faBullhorn,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import '../styles/SendNotificationCenter.css';

const SendNotificationForm = ({ onClose, onSuccess }) => {
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  
  // Form states
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('info');
  const [recipientType, setRecipientType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isImportant, setIsImportant] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // User and role lists
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  
  // UI states
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [titleCharCount, setTitleCharCount] = useState(0);
  
  const userDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);
  
  // Max lengths
  const MAX_TITLE_LENGTH = 100;
  const MAX_MESSAGE_LENGTH = 500;

  // Get user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        const authData = {
          user_id: parsedUser.id,
          username: parsedUser.username,
          role: parsedUser.role
        };
        setAuthToken(btoa(JSON.stringify(authData)));
        
        // Fetch users and roles
        fetchUsersAndRoles(authToken);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  // Fetch users and roles
  const fetchUsersAndRoles = async (token) => {
    if (!token) return;
    
    setLoadingUsers(true);
    try {
      // Fetch users
      const userResponse = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'list_users' })
      });
      
      const userData = await userResponse.json();
      if (userData.success && userData.users) {
        setUsers(userData.users);
        
        // Extract unique roles
        const uniqueRoles = [...new Set(userData.users.map(u => u.role))].filter(Boolean);
        setRoles(uniqueRoles);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users based on search
  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.fullname?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Handle user selection
  const toggleUser = (username) => {
    setSelectedUsers(prev => 
      prev.includes(username) 
        ? prev.filter(u => u !== username)
        : [...prev, username]
    );
  };

  // Handle role selection
  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  // Get notification type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return faCheckCircle;
      case 'warning': return faExclamationTriangle;
      case 'error': return faTimes;
      case 'system': return faServer;
      default: return faInfoCircle;
    }
  };

  // Get notification type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return '#25d366';
      case 'warning': return '#ff9500';
      case 'error': return '#ff3b30';
      case 'system': return '#6366f1';
      default: return '#4a9eff';
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }
    
    if (recipientType === 'specific' && selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }
    
    if (recipientType === 'role_based' && selectedRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }
    
    setSending(true);
    setError(null);
    
    try {
      const payload = {
        action: 'send',
        title: title.trim(),
        message: message.trim(),
        type: notificationType,
        recipient_type: recipientType,
        important: isImportant ? 1 : 0,
        sender_name: user?.fullname || user?.username || 'System'
      };
      
      if (recipientType === 'specific') {
        payload.recipients = selectedUsers;
      } else if (recipientType === 'role_based') {
        payload.recipients = selectedRoles;
      }
      
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php?action=send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setError(null);
        
        // Reset form
        setTitle('');
        setMessage('');
        setSelectedUsers([]);
        setSelectedRoles([]);
        setIsImportant(false);
        setCharCount(0);
        setTitleCharCount(0);
        
        // Callback
        if (onSuccess) {
          onSuccess(data);
        }
        
        // Auto close after 3 seconds
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      } else {
        setError(data.message || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('An error occurred while sending the notification');
    } finally {
      setSending(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setTitle('');
    setMessage('');
    setNotificationType('info');
    setRecipientType('all');
    setSelectedUsers([]);
    setSelectedRoles([]);
    setIsImportant(false);
    setError(null);
    setSuccess(false);
    setCharCount(0);
    setTitleCharCount(0);
  };

  return (
    <div className="send-notification-overlay" onClick={onClose}>
      <div className="send-notification-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title">
            <FontAwesomeIcon icon={faBullhorn} className="modal-title-icon" />
            <h2>Send Notification</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>Notification sent successfully!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="notification-form">
          {/* Title Field */}
          <div className="form-group">
            <label htmlFor="title">
              <FontAwesomeIcon icon={faEnvelope} />
              Title <span className="required">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= MAX_TITLE_LENGTH) {
                  setTitle(val);
                  setTitleCharCount(val.length);
                }
              }}
              placeholder="Enter notification title..."
              maxLength={MAX_TITLE_LENGTH}
              required
            />
            <span className="char-counter">{titleCharCount}/{MAX_TITLE_LENGTH}</span>
          </div>

          {/* Message Field */}
          <div className="form-group">
            <label htmlFor="message">
              <FontAwesomeIcon icon={faComment} />
              Message <span className="required">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= MAX_MESSAGE_LENGTH) {
                  setMessage(val);
                  setCharCount(val.length);
                }
              }}
              placeholder="Type your notification message..."
              rows={4}
              maxLength={MAX_MESSAGE_LENGTH}
              required
            />
            <span className="char-counter">{charCount}/{MAX_MESSAGE_LENGTH}</span>
          </div>

          {/* Notification Type */}
          <div className="form-group">
            <label>Notification Type</label>
            <div className="type-selector">
              {['info', 'success', 'warning', 'error', 'system'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`type-option ${notificationType === type ? 'active' : ''}`}
                  style={{
                    borderColor: notificationType === type ? getTypeColor(type) : 'transparent',
                    backgroundColor: notificationType === type ? getTypeColor(type) + '20' : 'transparent'
                  }}
                  onClick={() => setNotificationType(type)}
                >
                  <FontAwesomeIcon icon={getTypeIcon(type)} style={{ color: getTypeColor(type) }} />
                  <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recipient Type */}
          <div className="form-group">
            <label>Send To</label>
            <div className="recipient-selector">
              <button
                type="button"
                className={`recipient-option ${recipientType === 'all' ? 'active' : ''}`}
                onClick={() => {
                  setRecipientType('all');
                  setSelectedUsers([]);
                  setSelectedRoles([]);
                }}
              >
                <FontAwesomeIcon icon={faUsers} />
                <span>All Users</span>
              </button>
              <button
                type="button"
                className={`recipient-option ${recipientType === 'specific' ? 'active' : ''}`}
                onClick={() => setRecipientType('specific')}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>Specific Users</span>
              </button>
              <button
                type="button"
                className={`recipient-option ${recipientType === 'role_based' ? 'active' : ''}`}
                onClick={() => setRecipientType('role_based')}
              >
                <FontAwesomeIcon icon={faUserTag} />
                <span>By Role</span>
              </button>
            </div>
          </div>

          {/* Specific Users Selection */}
          {recipientType === 'specific' && (
            <div className="form-group user-selection">
              <label>
                <FontAwesomeIcon icon={faUserPlus} />
                Select Users <span className="required">*</span>
              </label>
              <div className="user-search-wrapper" ref={userDropdownRef}>
                <div className="search-input-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onFocus={() => setShowUserDropdown(true)}
                    placeholder="Search users..."
                    className="user-search-input"
                  />
                  <button
                    type="button"
                    className="dropdown-toggle-btn"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                  >
                    <FontAwesomeIcon icon={showUserDropdown ? faChevronUp : faChevronDown} />
                  </button>
                </div>
                
                {showUserDropdown && (
                  <div className="user-dropdown">
                    {loadingUsers ? (
                      <div className="dropdown-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading users...</span>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="dropdown-empty">No users found</div>
                    ) : (
                      filteredUsers.map((u) => (
                        <div
                          key={u.id}
                          className={`user-item ${selectedUsers.includes(u.username) ? 'selected' : ''}`}
                          onClick={() => toggleUser(u.username)}
                        >
                          <div className="user-avatar-small">
                            {u.fullname?.[0] || u.username?.[0] || 'U'}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{u.fullname || u.username}</span>
                            <span className="user-username">@{u.username}</span>
                          </div>
                          <span className="user-role-badge">{u.role}</span>
                          {selectedUsers.includes(u.username) && (
                            <FontAwesomeIcon icon={faCheckCircle} className="selected-check" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              {/* Selected Users Tags */}
              {selectedUsers.length > 0 && (
                <div className="selected-tags">
                  {selectedUsers.map((username) => {
                    const userObj = users.find(u => u.username === username);
                    return (
                      <span key={username} className="user-tag">
                        {userObj?.fullname || username}
                        <button
                          type="button"
                          onClick={() => toggleUser(username)}
                          className="remove-tag-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Role-Based Selection */}
          {recipientType === 'role_based' && (
            <div className="form-group role-selection">
              <label>
                <FontAwesomeIcon icon={faUserTag} />
                Select Roles <span className="required">*</span>
              </label>
              <div className="role-selector-wrapper" ref={roleDropdownRef}>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onFocus={() => setShowRoleDropdown(true)}
                    placeholder="Search roles..."
                    className="user-search-input"
                  />
                  <button
                    type="button"
                    className="dropdown-toggle-btn"
                    onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                  >
                    <FontAwesomeIcon icon={showRoleDropdown ? faChevronUp : faChevronDown} />
                  </button>
                </div>
                
                {showRoleDropdown && (
                  <div className="role-dropdown">
                    {roles.length === 0 ? (
                      <div className="dropdown-empty">No roles found</div>
                    ) : (
                      roles
                        .filter(r => r.toLowerCase().includes(userSearch.toLowerCase()))
                        .map((role) => (
                          <div
                            key={role}
                            className={`role-item ${selectedRoles.includes(role) ? 'selected' : ''}`}
                            onClick={() => toggleRole(role)}
                          >
                            <FontAwesomeIcon icon={faUserTag} />
                            <span className="role-name">{role}</span>
                            {selectedRoles.includes(role) && (
                              <FontAwesomeIcon icon={faCheckCircle} className="selected-check" />
                            )}
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
              
              {/* Selected Roles Tags */}
              {selectedRoles.length > 0 && (
                <div className="selected-tags">
                  {selectedRoles.map((role) => (
                    <span key={role} className="role-tag">
                      {role}
                      <button
                        type="button"
                        onClick={() => toggleRole(role)}
                        className="remove-tag-btn"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Important Toggle */}
          <div className="form-group important-toggle">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
              <span className="custom-checkbox">
                {isImportant && <FontAwesomeIcon icon={faCheck} />}
              </span>
              <FontAwesomeIcon icon={faStar} className={`star-icon ${isImportant ? 'active' : ''}`} />
              <span>Mark as Important</span>
            </label>
          </div>

          {/* Recipient Summary */}
          <div className="recipient-summary">
            <div className="summary-item">
              <span className="summary-label">Recipients:</span>
              <span className="summary-value">
                {recipientType === 'all' && 'All Users'}
                {recipientType === 'specific' && `${selectedUsers.length} user(s) selected`}
                {recipientType === 'role_based' && `${selectedRoles.length} role(s) selected`}
              </span>
            </div>
            {recipientType === 'specific' && selectedUsers.length > 0 && (
              <div className="summary-item">
                <span className="summary-label">Users:</span>
                <span className="summary-value">{selectedUsers.join(', ')}</span>
              </div>
            )}
            {recipientType === 'role_based' && selectedRoles.length > 0 && (
              <div className="summary-item">
                <span className="summary-label">Roles:</span>
                <span className="summary-value">{selectedRoles.join(', ')}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" className="reset-btn" onClick={resetForm}>
              <FontAwesomeIcon icon={faTimes} />
              Reset
            </button>
            <button
              type="submit"
              className="send-btn"
              disabled={sending || !title.trim() || !message.trim()}
            >
              {sending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Sending...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendNotificationForm;